import { MolhamSingleTargetable, TTargetableTabs } from "apiTypes";
import useRTL from "customHooks/useRTL";
import useTranslation from "next-translate/useTranslation";
import { useRouter } from "next/router";
import { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { DonateDetailsContext } from "../../../context/DonateDetailsContext";
import { baseReactingApiUrl } from "../../../utils/baseReactingApiUrl";
import { postData } from "../../../utils/postData";
import AddEditCommentModal from "../CommentsForm/AddEditCommentModal";
import AddCommentReplyModal from "../CommentsForm/AddEditReplyModal";
import CommentContainer from "../CommentsForm/CommentContainer";
import MolhamTabs from "../MolhamTabs";
import DonationAccordionTab from "./DonationAccordionTab";
import LastUpdatesTab from "./LastUpdatesTab";
import OverViewTab from "./OverViewTab";

type Props = {
  singleCase: MolhamSingleTargetable;
  url: {
    endPoint: string;
    id: string;
    query: string;
  };
};

const TargetableTabs = ({ singleCase, url }: Props) => {
  const {
    reactingCounts,
    commentsTab,
    setCommentsTab,
    setReactingCounts,
    replyTab,
    setReplyTab,
  } = useContext(DonateDetailsContext);
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation("common");
  const [canSwipeTabs, setCanSwipeTabs] = useState(true);
  const [isCommentSubmitted, setIsCommentSubmitted] = useState(false);
  const [showAddEditCommentModal, setShowAddEditCommentModal] = useState(false);
  const [showAddEditReplyModal, setShowAddEditReplyModal] = useState(false);
  const handleCloseAddEditCommentModal = () => {
    setShowAddEditCommentModal(false);
    setCommentsTab(false);
  };
  const handleCloseReplyModal = () => {
    setShowAddEditReplyModal(false);
    setReplyTab(null);
  };

  useEffect(() => {
    if (commentsTab) {
      setShowAddEditCommentModal(true);

      setIsCommentSubmitted(false);
    }
    if (replyTab !== null) {
      setShowAddEditReplyModal(true);
    }
  }, [commentsTab, replyTab]);

  const onCommentSubmit = async ({ comment }) => {
    setIsLoading(true);
    try {
      const res = await postData(baseReactingApiUrl(url, "comments"), {
        content: comment,
      });
      handleCloseAddEditCommentModal();

      setReactingCounts((prev) => ({
        ...prev,
        comments_count: prev.comments_count + 1,
      }));
      setIsCommentSubmitted(true);
      if (res.data?.error) {
        toast.error(res.data?.error);
        return;
      }

      toast.success(t(`comment_added`));
    } catch (err) {
      setIsCommentSubmitted(false);
      if (err?.response?.data.status === "Unauthorized") {
        toast.error(err.response.data.status);

        return;
      }

      toast.error(err?.message);

      toast.error(err.response?.data.error?.code);
    }
    setIsLoading(false);
  };
  const tabs = {
    overview: { label: "overview", icon: "fe-info" },
    body: {
      label: "sections_donate_details",
      icon: "fe-align-center",
    },

    updates: {
      label: "last_updates",
      icon: "fe-activity",
      badge: singleCase?.counters?.updates,
    },
    comments: {
      label: "comments",
      icon: "fe-message-circle",
      badge: reactingCounts?.comments_count,
    },
    donations: {
      label: "donations",
      icon: "fe-package",
      badge: singleCase?.counters?.donations,
    },
  };

  const visibleTabs = singleCase?.visible_tabs?.map((tab) => tabs[tab]);
  // function hasArabicLetters(word) {
  //   const arabicRegex = /[\u0600-\u06FF]/; // Range for Arabic Unicode characters
  //   return arabicRegex.test(word);
  // }
  // const isRTL = useRTL();
  // const detailsArabic =
  //   hasArabicLetters(singleCase?.contents?.body.split(" ")[0]) && !isRTL;

  // const detailsEnglish = !hasArabicLetters(singleCase?.contents?.body.split(" ")[0]) && isRTL;

  const inc = (str: TTargetableTabs) => singleCase?.visible_tabs.includes(str);
  const router = useRouter();
  const defaultIndexCalc = () => {
    if (inc("updates") && router.query.view_updates == "1") {
      return 2;
    } else if (inc("overview") && inc("body")) {
      return 1;
    } else return 0;
  };
  return (
    <>
      <MolhamTabs
        canSwipeTabs={canSwipeTabs}
        defaultIndex={defaultIndexCalc()}
        setActiveIndex={isCommentSubmitted && inc("body") ? 3 : undefined}
        TabsItems={visibleTabs}
      >
        {inc("overview") && <OverViewTab details={singleCase?.details} />}
        {inc("body") && (
          <div
            dir="auto"
            className="py-3 px-3 px-lg-0 lh17 fs-18"
            dangerouslySetInnerHTML={{
              __html: singleCase?.contents?.body,
            }}
            // style={
            //   detailsArabic
            //     ? { direction: "rtl", textAlign: "start" }
            //     : detailsEnglish
            //     ? { direction: "ltr", textAlign: "left" }
            //     : { textAlign: "unset", direction: "unset" }
            // }
          />
        )}

        {inc("updates") && (
          <div className="mt-3">
            <LastUpdatesTab
              onTouchEnd={() => setCanSwipeTabs(true)}
              onTouchStart={() => setCanSwipeTabs(false)}
            />
          </div>
        )}
        {inc("comments") && (
          <div className="mt-3">
            <CommentContainer isCommentSubmitted={isCommentSubmitted} />
          </div>
        )}
        {singleCase?.visible_tabs.includes("donations") && (
          <DonationAccordionTab />
        )}
      </MolhamTabs>
      <AddEditCommentModal
        showAddEditCommentModal={showAddEditCommentModal}
        handleCloseAddEditCommentModal={handleCloseAddEditCommentModal}
        onEditCommentSubmit={(data) => onCommentSubmit(data)}
        isLoading={isLoading}
        isAdd
      />
      <AddCommentReplyModal
        content={replyTab}
        handleClose={handleCloseReplyModal}
        isAdd
        showAddEditCommentModal={showAddEditReplyModal}
        mutate={() => setIsCommentSubmitted(true)}
      />
    </>
  );
};

export default TargetableTabs;
